import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RequestStatus } from '../../../config/store';
import useExternalTypeId from '../../../hooks/useExternalTypeId';
import { RootState } from '../../../store/rootStore';
import { getContracts } from '../store/licenses.store';
import { ContractData, LicenseData } from '../types/licenses';

const useLicenses = (): {
  licenses: LicenseData[];
  contracts: ContractData[];
  isLoading: boolean;
} => {
  const { externalIdLicense, externalTypeLicense, isLoaded, hasExternalId } = useExternalTypeId();
  const dispatch = useDispatch<any>();
  const contractsStatus = useSelector((state: RootState) => state.licenses.contracts.request.get.status);
  const contracts = useSelector((state: RootState) => state.licenses.contracts.data);

  React.useEffect(() => {
    if (contractsStatus === RequestStatus.Unsent && isLoaded && hasExternalId) {
      dispatch(
        getContracts({
          externalType: externalTypeLicense,
          externalId: externalIdLicense,
        })
      );
    }
  }, [dispatch, externalTypeLicense, externalIdLicense, isLoaded, hasExternalId, contractsStatus]);
  const transformedContracts = React.useMemo<ContractData[]>(
    () =>
      (contracts || []).map((cont: any) => ({
        ...cont,
        licenses: cont.licenses
          .map(
            (license: any) =>
              ({
                ...license,
                is_expired: !license.expires_at ? false : new Date(license.expires_at) < new Date(),
              } as LicenseData)
          )
          .filter((l: any) => {
            return (!l.is_expired || l.assigned_to) && !(!l.assigned_to && !l.assignments_left);
          }),
      })),
    [contracts]
  );
  const licenses = React.useMemo(
    () =>
      transformedContracts.flatMap((cont: any) => cont.licenses.map((c: any) => ({ ...c, contract: cont }))) as LicenseData[],
    [transformedContracts]
  );
  return {
    contracts: transformedContracts,
    licenses,
    isLoading: hasExternalId && [RequestStatus.Pending, RequestStatus.Unsent].includes(contractsStatus),
  };
};

export default useLicenses;
